import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'react-redux';
import type { RootState } from '../../redux/store';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  setConfigPhase, setConfigPlan, setConfigScript, setConfigFilling, setConfigWindow, addShown, cancelConfig, setToast,
  selectConfigSelections, selectConfigPerCell, selectConfigWeekPortions,
  selectConfigTargets,
  DEFAULT_SELECTION,
} from '../../redux/mealPlanUISlice';
import { dietKindOf } from '../weekly/v2/dietKind';
import {
  replaceSlotRecipe, clearSlot, selectCurrentWeekStart, type PlannedMeal,
} from '../../redux/mealPlanSlice';
import { isRecipeMeal } from '../../redux/mealEntry';
import { useReadableRecipeIndex } from '../../hooks/useReadableRecipeIndex';
import { revealOrder, rowOf, dayOf, THINK_MS, STAG_MS_DESKTOP } from '../weekly/v2/constants';
import { fitsMealType } from './fitsMealType';
import type { RecipeIndexEntry } from '../../services/unifiedRecipeService';
import { addDays, dayIndexInWeek, weekStartOf } from '../../utils/localDate';
import { nowAnchor } from '../weekly/v2/nextMeal';
import {
  historyFromMeals, SAME_MEAL_LOOKBACK_DAYS,
} from '../../services/mealIdentity';
import { selectMealsForDateRange } from '../../redux/mealPlanSlice';
import { selectMealSchedule } from '../../redux/userPreferencesSlice';
import { planSlotFor } from '../../services/planRanking';

import { useCookPreferences } from '../../hooks/useCookPreferences';
import { explainGenerationPlan, type AffinityScript } from './affinityScript';
import { useAffinityContext } from './useAffinityContext';
import { buildGenerationPlan, generationSlotsMap, type GenerationOptions, type GenerationPlan } from './generationPlan';

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner'] as const;

export { fitsMealType };
export { buildGenerationPlan, type GenerationOptions, type GenerationPlan };

/**
 * Generation timeline timers. Module-scoped (NOT per hook instance): starting a
 * generation flips configPhase, which unmounts the screen that initiated it
 * (WeeklyDesktop → MealConfig switch in TheWeeklyScreen) — the timeline must
 * survive that unmount. Timers only dispatch Redux actions, never component
 * state. Starting a new generation clears any pending one.
 */
let generationTimers: ReturnType<typeof setTimeout>[] = [];

/** Drops every pending step of the timeline. Safe to call when none is running. */
export function clearGenerationTimers(): void {
  generationTimers.forEach(clearTimeout);
  generationTimers = [];
}

/**
 * Which slots to (re)generate: explicit config selections if any; otherwise
 * the slots currently holding a recipe meal (regenerate what's planned);
 * otherwise the default weekly dinners. Non-recipe entries are never included.
 */
export function resolveGenerationSelections(
  configSelections: number[],
  slotsMap: Map<number, PlannedMeal>,
): number[] {
  if (configSelections.length > 0) return configSelections;
  const recipeSlots = [...slotsMap.entries()]
    .filter(([, m]) => isRecipeMeal(m))
    .map(([sk]) => sk)
    .sort((a, b) => a - b);
  if (recipeSlots.length > 0) return recipeSlots;
  return DEFAULT_SELECTION; // slotKeys 14..20 (souper row)
}

/**
 * When an empty meal of today stops being worth filling, in minutes past
 * midnight: breakfast at 10:00, lunch at 14:00, dinner at 21:00 (people eat
 * late). Until #808 the whole day stayed open until 21:00, so at 20:00 one tap
 * replaced the lunch already eaten. A meal already planned for today is never
 * replaced, whatever the hour (#862).
 */
export const MEAL_CUTOFF_MINUTES = [10 * 60, 14 * 60, 21 * 60] as const;

/** Tonight's dinner stays open until 21:00, then counts as past. */
export const TONIGHT_CUTOFF_MINUTES = MEAL_CUTOFF_MINUTES[2];

/** Today as the generator reads it: the calendar day and how far into it we are. */
export interface GenerationClock {
  today: string;
  nowMinutes: number;
}

/** Whether a meal on `date`, in row `row` (0 breakfast, 1 lunch, 2 dinner), is still to come. */
export function isSlotOpen(date: string, row: number, clock: GenerationClock): boolean {
  if (date !== clock.today) return date > clock.today;
  return clock.nowMinutes < MEAL_CUTOFF_MINUTES[row];
}

/**
 * Drop the slots whose meal is over (#744, #808).
 *
 * Régénérer took "every slot holding a recipe" of the Monday week, so on
 * Friday 25 September one tap replaced Monday to Thursday — dinners already
 * eaten — with no confirmation and no undo, and the record of what was cooked
 * was gone for good. A past meal is never the generator's to write, whether the
 * slot was picked by default or ticked on the config screen. Today's meals each
 * close at their own hour (`MEAL_CUTOFF_MINUTES`).
 */
export function dropPastSlots(selections: number[], start: string, clock: GenerationClock): number[] {
  return selections.filter((sk) => isSlotOpen(addDays(start, dayOf(sk)), rowOf(sk), clock));
}

/**
 * The slots holding a meal already cooked, whatever its day (#795).
 *
 * #744 kept Régénérer off the past, but tonight stays open until 21:00: cook
 * at 19:00, write « plus de sauce », tap Régénérer, and the dinner and its note
 * were replaced. A cooked meal is a record, not a plan, so its slot is never
 * the generator's — on any day, picked by default or ticked.
 */
export function cookedSlotsOf(meals: PlannedMeal[], start: string): Set<number> {
  const cooked = new Set<number>();
  for (const meal of meals) {
    if (meal.status !== 'cooked') continue;
    const row = (MEAL_TYPES as readonly string[]).indexOf(meal.mealType);
    if (row === -1) continue;
    const day = dayIndexInWeek(start, meal.scheduledFor);
    if (day !== null) cooked.add(row * 7 + day);
  }
  return cooked;
}

/**
 * The slots of today that hold a meal (#862).
 *
 * On Thursday 29 October at 18:00 a partner tapped Régénérer, and the dinner on
 * the other phone's screen, recipe open, became a salmon burger: tonight was
 * open until 21:00. What is planned for today is decided, on any device and at
 * any hour, picked by default or ticked (the config screen ticks every dinner).
 * The cook changes it from its own sheet.
 */
export function plannedTodaySlots(slotsMap: Map<number, PlannedMeal>, start: string, today: string): Set<number> {
  return new Set([...slotsMap.keys()].filter((sk) => addDays(start, dayOf(sk)) === today));
}

/**
 * The slots one tap on Régénérer writes: past meals, cooked meals and today's
 * planned meals left out.
 *
 * "Regenerate what's planned" alone is not enough once the past is off limits:
 * on a Friday whose only recipes sit on Monday to Thursday, every planned slot
 * is past and the button would answer "those days are over" beside three empty
 * evenings. When nothing planned is still open, the open dinners are what is
 * left to fill. Slots ticked on the config screen are never widened.
 */
export function openGenerationSelections(
  configSelections: number[],
  slotsMap: Map<number, PlannedMeal>,
  start: string,
  clock: GenerationClock,
  cookedSlots: ReadonlySet<number> = new Set(),
): number[] {
  const plannedToday = plannedTodaySlots(slotsMap, start, clock.today);
  const writable = (sels: number[]) =>
    dropPastSlots(sels, start, clock).filter((sk) => !cookedSlots.has(sk) && !plannedToday.has(sk));
  const open = writable(resolveGenerationSelections(configSelections, slotsMap));
  if (open.length > 0 || configSelections.length > 0) return open;
  return writable(DEFAULT_SELECTION);
}

/** The first day a run writes, for the caption; the window's start when it writes nothing. */
export function firstRegeneratedDay(selections: number[], start: string): string {
  if (selections.length === 0) return start;
  return addDays(start, Math.min(...selections.map(dayOf)));
}

/** Why a tap has nothing to write: today's meal is kept, or the days are over. */
export function nothingToRegenerateKey(
  slotsMap: Map<number, PlannedMeal>,
  start: string,
  clock: GenerationClock,
): 'thisWeek.regenerateTodayKept' | 'thisWeek.regeneratePastOnly' {
  return plannedTodaySlots(slotsMap, start, clock.today).size > 0
    ? 'thisWeek.regenerateTodayKept'
    : 'thisWeek.regeneratePastOnly';
}

/** Saturday or Sunday — what the taste score calls a weekend, read off the date. */
export function isWeekendDate(date: string): boolean {
  return (dayIndexInWeek(weekStartOf(date), date) ?? 0) >= 5;
}

/** Minimal dispatch shape — the timeline only ever dispatches plain actions. */
type Dispatch = (action: any) => void;

export interface TimelineArgs {
  /** slotKey → recipe id, as produced by buildGenerationPlan. */
  plan: Record<number, string>;
  /** The slots the plan filled — what the cascade reveals, then commits. */
  fillable: number[];
  recipeMap: Record<string, RecipeIndexEntry>;
  /** The day slot column 0 falls on: a Monday, or today on the phone (#808). */
  start: string;
  perCell: Record<number, { portions?: number; kind?: string }>;
  weekPortions: number;
  /** Empties the slots the generator honestly could not fill (#528). */
  commitUnfilled: () => void;
  /** What the « Les affinités » panel plays while thinking. */
  script?: AffinityScript | null;
}

/**
 * The reveal timeline: think, then stagger the cells in, then commit everything
 * at the last step. Nothing touches mealPlanSlice before that last timer fires,
 * which is what makes cancelling mid-flight a clean no-op on the plan.
 *
 * Exported (rather than inlined in the hook) so the cancel path is testable
 * without rendering React.
 */
export function startGenerationTimeline(dispatch: Dispatch, args: TimelineArgs): void {
  const { plan, fillable, recipeMap, start, perCell, weekPortions, commitUnfilled, script = null } = args;

  dispatch(setConfigScript(script));
  dispatch(setConfigFilling(fillable));
  dispatch(setConfigWindow(start));
  dispatch(setConfigPhase('thinking'));
  generationTimers.push(setTimeout(() => {
    dispatch(setConfigPlan(plan));
    dispatch(setConfigPhase('cascade'));
    const order = revealOrder(fillable);
    order.forEach((sk, i) => {
      generationTimers.push(setTimeout(() => {
        dispatch(addShown(sk));
        if (i === order.length - 1) {
          // Commit directly — no validation step. Each meal is filed under the
          // Monday bucket of its own date, so a window across Monday writes two.
          fillable.forEach((slot) => {
            const recipe = recipeMap[plan[slot]];
            if (!recipe) return;
            dispatch(replaceSlotRecipe({
              recipe: recipe as any,
              scheduledFor: addDays(start, dayOf(slot)),
              servings: perCell[slot]?.portions ?? weekPortions,
              mealType: MEAL_TYPES[rowOf(slot)],
            }));
          });
          commitUnfilled();
          dispatch(setConfigPhase('idle'));
        }
      }, STAG_MS_DESKTOP * (i + 1)));
    });
  }, THINK_MS));
}

/**
 * Abort a generation and leave the planner. Because the timeline commits only
 * on its last step, cancelling before then leaves the week exactly as it was —
 * there is nothing to roll back. Also the way out of the config screen when no
 * generation is running (no timers pending: clearing them is a no-op).
 */
export function cancelGeneration(dispatch: Dispatch): void {
  clearGenerationTimers();
  dispatch(cancelConfig());
}

export interface GenerateWeekOptions {
  /**
   * Run on the seven days from this date instead of the page's Monday week
   * (#808): the phone passes today, so Régénérer covers the days its strip
   * shows, across two Monday buckets when they span one. A window ignores the
   * config screen's ticks and per-cell settings — those are keyed to the
   * Monday week the config screen showed — and regenerates what is planned in
   * it, or its open dinners.
   */
  windowStart?: string;
}

/**
 * Generate-and-commit. No validation step: when the cascade finishes, the
 * plan is already committed to mealPlanSlice and phase returns to 'idle'
 * (the weekly view). Regenerating simply runs this again.
 */
export function useGenerateWeek(options: GenerateWeekOptions = {}) {
  const { windowStart } = options;
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const configSelections = useAppSelector(selectConfigSelections);
  const configPerCellSetting = useAppSelector(selectConfigPerCell);
  const weekPortions = useAppSelector(selectConfigWeekPortions);
  const configTargets = useAppSelector(selectConfigTargets);
  const prefs = useCookPreferences();
  const recipeIndex = useReadableRecipeIndex();
  const weekStart = useAppSelector(selectCurrentWeekStart);
  const affinity = useAffinityContext();
  const mealSchedule = useAppSelector(selectMealSchedule);
  const { t } = useTranslation();

  const generate = useCallback(() => {
    if (recipeIndex.length === 0) return;
    clearGenerationTimers();

    const start = windowStart ?? weekStart;
    const selections = windowStart ? [] : configSelections;
    const configPerCell = windowStart ? {} : configPerCellSetting;

    // Read by date across every bucket, not only the page's: a meal can sit in
    // another week's bucket, a window can span two, and a cooked dinner filed
    // elsewhere is still tonight's dinner.
    const windowMeals = selectMealsForDateRange(store.getState(), start, addDays(start, 6));
    const slotsMap = generationSlotsMap(windowMeals, start);
    const cookedSlots = cookedSlotsOf(windowMeals, start);
    const clock = nowAnchor();
    const sels = openGenerationSelections(selections, slotsMap, start, clock, cookedSlots);
    if (sels.length === 0) {
      // Nothing left to write — a week wholly over, only today's meal left, or
      // only such slots ticked: say so and go back to the week, rather than
      // leave a button that looks dead.
      dispatch(setToast({ text: t(nothingToRegenerateKey(slotsMap, start, clock)) }));
      dispatch(setConfigPhase('idle'));
      return;
    }

    // The axes shape the distribution only while the cook has not touched the
    // config screen's own targets; the full preference resolver always shapes
    // the picks, and its hard rules (allergies, intolerances, excluded
    // ingredients, declared diets) always hold — a week regenerated after
    // onboarding must not undo the promise the onboarding week made.
    const isDefaultTargets = configTargets.fish === 2 && configTargets.veg === 2 && configTargets.meat === 1;
    // Two weeks either side, minus the slots being regenerated — they are
    // about to be replaced, so they are not something to keep away from.
    const dateOf = (sk: number) => addDays(start, dayOf(sk));
    const regenerated = new Set(sels.map((sk) => `${dateOf(sk)}:${MEAL_TYPES[rowOf(sk)]}`));
    const history = historyFromMeals(
      selectMealsForDateRange(
        store.getState(),
        addDays(start, -SAME_MEAL_LOOKBACK_DAYS),
        addDays(start, 6 + SAME_MEAL_LOOKBACK_DAYS),
      ).filter((m) => isRecipeMeal(m) && !regenerated.has(`${m.scheduledFor}:${m.mealType}`)),
    );
    const planOptions: GenerationOptions = {
      sameMeal: { history, dateOf },
      // The schedule describes dinners (the souper row) — the same days,
      // kinds and time caps the planner reads.
      slotOf: (sk) => (rowOf(sk) === 2 ? planSlotFor(dateOf(sk), mealSchedule) : null),
      perCell: configPerCell,
      targets: prefs.targets && isDefaultTargets ? prefs.targets : configTargets,
      kindOf: dietKindOf,
      // Column 5 is a Saturday only in a Monday week; a window from today is not.
      prefer: (r, sk) => prefs.score(r, { isWeekend: isWeekendDate(dateOf(sk)) }),
      exclude: prefs.hasHardRules ? (r) => prefs.isExcluded(r) : undefined,
    };
    const { plan, unfilled, reviewed } = buildGenerationPlan(sels, recipeIndex, slotsMap, planOptions);
    const fillable = Object.keys(plan).map(Number);

    /**
     * A slot we could not fill must not keep what it held: regenerating a
     * breakfast slot occupied by a dinner recipe (#528) has to empty it, not
     * silently leave the wrong meal in place. The empty row then says why
     * (DayList derives that from the library, so nothing to store here).
     */
    const commitUnfilled = () => {
      unfilled.forEach((sk) => {
        const current = slotsMap.get(sk);
        if (!current || !isRecipeMeal(current)) return;
        dispatch(clearSlot({
          scheduledFor: dateOf(sk),
          mealType: MEAL_TYPES[rowOf(sk)],
        }));
      });
    };

    if (fillable.length === 0) {
      // Nothing to reveal (e.g. only breakfast slots selected on a library with
      // no morning recipe). Still return to the week view — staying on the
      // config screen with no feedback would read as a dead button.
      commitUnfilled();
      dispatch(setConfigPhase('idle'));
      return;
    }

    const recipeMap: Record<string, RecipeIndexEntry> = {};
    recipeIndex.forEach((r) => { recipeMap[r.id] = r; });

    // The panel is a flourish over a plan that already exists: if explaining
    // it fails, the generation must still run — just without the story.
    let script: AffinityScript | null = null;
    try {
      script = explainGenerationPlan(
        { plan, recipeIndex, reviewed },
        { ...affinity, targets: planOptions.targets, exclude: planOptions.exclude, kindOf: dietKindOf },
      );
    } catch (error) {
      console.warn('[generation] affinity script failed', error instanceof Error ? error.message : error);
    }

    startGenerationTimeline(dispatch, {
      plan,
      fillable,
      recipeMap,
      start,
      perCell: configPerCell,
      weekPortions,
      commitUnfilled,
      script,
    });
  }, [dispatch, store, windowStart, configSelections, recipeIndex, weekStart, configPerCellSetting, weekPortions, configTargets, prefs, affinity, mealSchedule, t]);

  /** Abort whatever is running and return to the week view. */
  const cancel = useCallback(() => cancelGeneration(dispatch), [dispatch]);

  return { generate, cancel };
}
